import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Eye, EyeOff, Mail, Lock, User, HelpCircle, ArrowLeft } from 'lucide-react';
// Gift comentado - ya no se usa el código de influencer en el registro
import Logo from './Logo';
import LanguageSelector from './LanguageSelector';
import './Login.css';

export default function Register() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // CÓDIGO DE INFLUENCER COMENTADO - Se maneja en la pantalla de pago
  // const [influencerCode, setInfluencerCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { signup, signInWithGoogle } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // CÓDIGO DE INFLUENCER COMENTADO - Se maneja en la pantalla de pago
  // El código de influencer se aplica cuando el usuario ingresa el cupón
  // en la pantalla previa al pago con Stripe, no durante el registro
  /*
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setInfluencerCode(refCode);
    }
  }, [searchParams]);
  */

  function validateForm() {
    const newErrors = {};

    if (!username.trim()) {
      newErrors.username = t('register.usernameRequired');
    } else if (username.length < 3) {
      newErrors.username = t('register.usernameMinLength');
    }

    if (!email.trim()) {
      newErrors.email = t('register.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = t('register.emailInvalid');
    }

    if (!password) {
      newErrors.password = t('register.passwordRequired');
    } else if (password.length < 6) {
      newErrors.password = t('register.passwordMinLength');
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = t('register.confirmPasswordRequired');
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = t('register.passwordsDoNotMatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      // CÓDIGO DE INFLUENCER COMENTADO - Se maneja en la pantalla de pago
      await signup(email, password, username, null);
      
      // Esperar un momento para que se cargue el perfil del usuario
      // antes de navegar al dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
    } catch (error) {
      console.error('Error en registro:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    try {
      setLoading(true);
      await signInWithGoogle();
      navigate('/dashboard');
    } catch (error) {
      console.error('Error en Google Sign-In:', error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <LanguageSelector />
          <Logo size="auth-logo" className="auth-logo" />
          <h2>{t('register.title')}</h2>
          <p>{t('register.subtitle')}</p>
          <button 
            type="button" 
            className="back-to-landing-link"
            onClick={() => (window.location.href = 'https://rtb-collect.com')}
          >
            <ArrowLeft size={16} /> {t('register.backToLanding')}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="auth-form register-form">
          {/* Google Auth como primera opción */}
          <button
            type="button"
            className="google-signin-button"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {loading ? t('register.creatingAccount') : t('register.continueWithGoogle')}
          </button>

          <div className="auth-divider">
            <span>o</span>
          </div>

          {/* Formulario de registro reorganizado para 3 columnas */}
          <div className="form-group">
            <label htmlFor="username">{t('register.username')}</label>
            <div className="input-wrapper">
              <User size={20} className="input-icon" />
              <input
                id="username"
                type="text"
                className={`input ${errors.username ? 'error' : ''}`}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t('register.usernamePlaceholder')}
                autoComplete="username"
                required
              />
            </div>
            {errors.username && <span className="error-message">{errors.username}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('common.email')}</label>
            <div className="input-wrapper">
              <Mail size={20} className="input-icon" />
              <input
                id="email"
                type="email"
                className={`input ${errors.email ? 'error' : ''}`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.emailPlaceholder')}
                autoComplete="email"
                required
              />
            </div>
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('common.password')}</label>
            <div className="input-wrapper">
              <Lock size={20} className="input-icon" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className={`input ${errors.password ? 'error' : ''}`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.passwordPlaceholder')}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">{t('register.confirmPassword')}</label>
            <div className="input-wrapper">
              <Lock size={20} className="input-icon" />
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                className={`input ${errors.confirmPassword ? 'error' : ''}`}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('register.confirmPasswordPlaceholder')}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
          </div>

          {/* CÓDIGO DE INFLUENCER COMENTADO - Se maneja en la pantalla de pago */}
          {/* El código de influencer se aplica cuando el usuario ingresa el cupón */}
          {/* en la pantalla previa al pago con Stripe, no durante el registro */}
          {/*
          <div className="form-group">
            <label htmlFor="influencerCode">Código de influencer (opcional)</label>
            <div className="input-wrapper">
              <Gift size={20} className="input-icon" />
              <input
                id="influencerCode"
                type="text"
                className="input"
                value={influencerCode}
                onChange={(e) => setInfluencerCode(e.target.value.toUpperCase())}
                placeholder="Código de influencer"
              />
            </div>
            {influencerCode && (
              <div className="influencer-badge">
                🎁 ¡Obtendrás descuentos especiales!
              </div>
            )}
          </div>
          */}

          <button
            type="submit"
            className="btn btn-primary auth-button"
            disabled={loading}
          >
            {loading ? t('register.creatingAccount') : t('register.createAccount')}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            {t('register.alreadyHaveAccount')}{' '}
            <Link to="/login" className="auth-link">
              {t('register.loginHere')}
            </Link>
          </p>
          <button 
            className="how-it-works-button"
            onClick={() => window.open('/how-it-works', '_blank')}
          >
            <HelpCircle size={16} />
            {t('register.howItWorks')}
          </button>
        </div>
      </div>
      
      {/* Panel de imágenes lateral */}
      <div className="auth-image-panel">
        <div className="auth-image-background" style={{ backgroundImage: `url('/monterrey-bbva.jpeg')` }}>
          <div className="dark-gradient"></div>
          <img src="/rtb-monterrey.jpg" alt="RTB Monterrey" className="auth-overlay-image" />
        </div>
      </div>
    </div>
  );
}
